/**
 * useSquareMenu — the live, orderable menu for the ordering screen.
 *
 * Fetches /api/square-menu (Square catalog: items, variations, modifiers,
 * images, sold-out, online visibility), merges the curated presentation layer
 * from menu-data.json on top, and returns a ready-to-render grouped menu.
 *
 * Square is the source of truth for price/availability/modifiers; the curated
 * overlay only adds descriptions, images, emoji, and combo flavor.
 *
 * Returns { menu, categories, loading, error, refetch, env }.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { mergeCuratedMenu } from '../ordering/curatedOverlay';

const API_BASE = process.env.REACT_APP_API_BASE
  || (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001'
    : 'https://my-app-ramakrishna5s-projects.vercel.app');

// Read the production catalog by default so the screen shows real items.
// Checkout stays environment-aware separately.
const MENU_ENV = process.env.REACT_APP_SQUARE_MENU_ENV || 'production';
const CURATED_JSON_URL = '/menu-data.json';
const CACHE_KEY = 'db_square_menu_v1';

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// Stable empty array so `categories` doesn't change identity on every render
// while the menu is loading (consumers put it in effect/memo deps).
const NO_CATEGORIES = [];

/**
 * `shared: true` (the public /menu page) asks for the edge-cacheable variant —
 * sold-out state may be up to ~a minute old, but visitors stop paying two
 * Firestore reads each. Staff screens leave it off and read overlays live.
 */
export function useSquareMenu({ env = MENU_ENV, shared = false } = {}) {
  const cached = readCache();
  const [menu, setMenu] = useState(cached);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const fetchMenu = useCallback(async (fresh = false) => {
    setLoading(true);
    setError(null);
    try {
      // Square live menu + curated overlay in parallel. `fresh` skips the
      // server cache — used right after staff toggle item availability.
      const [sqRes, curatedRes] = await Promise.all([
        fetch(`${API_BASE}/api/square-menu?env=${encodeURIComponent(env)}${fresh ? '&fresh=1' : shared ? '&shared=1' : ''}`),
        fetch(CURATED_JSON_URL).catch(() => null),
      ]);

      const sqJson = await sqRes.json().catch(() => ({}));
      if (!sqRes.ok || !sqJson.ok) throw new Error(sqJson.error || `Menu unavailable (${sqRes.status})`);

      let curated = null;
      if (curatedRes && curatedRes.ok) curated = await curatedRes.json().catch(() => null);

      const merged = mergeCuratedMenu({ categories: sqJson.categories || [] }, curated);
      if (!mountedRef.current) return;
      setMenu(merged);
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(merged)); } catch { /* ignore quota */ }
    } catch (e) {
      if (mountedRef.current) setError(e.message || 'Could not load the menu');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [env, shared]);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  return {
    menu,
    categories: menu?.categories || NO_CATEGORIES,
    loading,
    error,
    refetch: fetchMenu,
    env,
  };
}

export default useSquareMenu;
